import { shiftAndZeroLeadingLeft, shiftArrayRight, shiftArrayLeft, findLastNonZeroIndex } from "./dataUtils";

import {
	findObjectByProperty,
	filterObjectsByProperty,
	getValuesByPropertyPath,
	deepClone,
	sumArrays,
} from "./dataUtils";
import { evaluateAccessLevel } from "./accessControlUtils";
import { debugLog } from "./debugUtils";

/**
 * Applies "Add/Cut" logic to an item's budget and quantity arrays.
 * Updates `item_change_in_budget`, `item_change_in_quantity`, and `item_rolling_total` for the item.
 *
 * @param {Object} data - The baseline/scenario item object. Must contain:
 *   - increment {number} (size of increment)
 *   - fielding_delay {number|string} (periods before impact; optional)
 *   - item_change_in_budget {number[]}
 *   - item_change_in_quantity {number[]}
 *   - item_rolling_total {number[]}
 *   - item_unit_cost {number[]}
 *   - total_existing_inventory {number}
 *   - item_baseline_quantity {number[]}
 *   - applyOsEscalation {function}
 *   - applyInflation {function}
 * @param {Object} option - The update option, must contain:
 *   - index {number} (which period to update)
 *   - value {number} (new quantity for that period)
 * @returns {Object} The updated item data object.
 *
 * @example
 * const updatedItem = applyAddCut(item, { index: 2, value: 10 });
 * // item_change_in_budget, item_change_in_quantity, and item_rolling_total are updated
 */
export const applyAddCut = (data, option) => {
	// Parameter safety checks
	if (
		!data ||
		!option ||
		typeof option.index !== "number" ||
		typeof option.value !== "number" ||
		!Array.isArray(data.item_change_in_budget) ||
		!Array.isArray(data.item_change_in_quantity) ||
		!Array.isArray(data.item_rolling_total)
	) {
		console.warn("applyAddCut: Invalid parameters", { data, option });
		return data || {};
	}

	const inc = data.increment * 1;
	const fieldingDelay = data.fielding_delay ? parseInt(data.fielding_delay) : 0;
	let arrBudget = [...data.item_change_in_budget];
	let arrQuantity = [...data.item_change_in_quantity];
	let arrRollingTotal = [...data.item_rolling_total];

	// Update quantity for the targeted period
	arrQuantity[option.index] = option.value;

	// Recalculate budget for each period
	arrBudget = arrBudget.map((x, idx) => {
		let value = (arrQuantity[idx] / inc) * data.item_unit_cost[idx];
		if (idx >= fieldingDelay) {
			value += applyOsEscalation(data, arrQuantity, idx, fieldingDelay) * applyInflation(data, idx);
		}
		return value;
	});

	// Update rolling total of inventory
	let tmpRollingTotal = 0;
	arrRollingTotal = arrRollingTotal.map((x, idx) => {
		if (idx === 0) tmpRollingTotal = data.total_existing_inventory;
		tmpRollingTotal += data.item_baseline_quantity[idx] + arrQuantity[idx];
		return tmpRollingTotal;
	});

	// Update data object with results
	data.item_change_in_budget = [...arrBudget];
	data.item_change_in_quantity = [...arrQuantity];
	data.item_rolling_total = [...arrRollingTotal];
	data.item_change_in_budget_total = arrBudget.reduce((a, b) => a + b, 0);
	data.item_change_in_quantity_total = arrQuantity.reduce((a, b) => a + b, 0);

	// ----------------------------------------------------------
	// OPTIONAL DEBUG BLOCK (specific to item id 486)
	// ----------------------------------------------------------
	if (data.id === 486) {
		debugLog(4, "[scenarioUtils][PROCESS] applyAddCut", {
			item_1: data,
			arraBudget: arrBudget,
			arrQuantity: arrQuantity,
			arrRollingTotal: arrRollingTotal,
			option: option,
		});
	}

	return data;
};

/**
 * Apply "Retire More/Less" logic to update item budget, quantity, and rolling totals.
 *
 * Updates arrays in `data` to reflect retirements for a given period (option.index) and value.
 *
 * @param {Object} data - The baseline/scenario item object, with required arrays and methods:
 *   - item_change_in_budget {number[]}
 *   - item_change_in_quantity {number[]}
 *   - item_rolling_total {number[]}
 *   - item_planned_retirement {number[]}
 *   - total_existing_inventory {number}
 *   - applyOsEscalation {function}
 *   - applyInflation {function}
 * @param {Object} option - The retire operation, must include:
 *   - index {number} - Period to update
 *   - value {number} - New quantity (retirement count) for the period
 * @returns {Object} Updated data object
 *
 * @example
 * const updated = applyRetireMoreLess(item, { index: 2, value: 3 });
 * // item_change_in_budget, item_change_in_quantity, and item_rolling_total updated
 */
export const applyRetireMoreLess = (data, option) => {
	// Parameter safety checks
	if (
		!data ||
		!option ||
		typeof option.index !== "number" ||
		typeof option.value !== "number" ||
		!Array.isArray(data.item_change_in_budget) ||
		!Array.isArray(data.item_change_in_quantity) ||
		!Array.isArray(data.item_rolling_total)
	) {
		console.warn("applyRetireMoreLess: Invalid parameters", { data, option });
		return data || {};
	}

	const fieldingDelay = 0; // Retirement logic has no delay

	let arrBudget = [...data.item_change_in_budget];
	let arrQuantity = [...data.item_change_in_quantity];
	let arrRollingTotal = [...data.item_rolling_total];

	// Set new retirement quantity for the target period
	arrQuantity[option.index] = option.value;

	// Recalculate the budget for each period based on retirement
	arrBudget = arrBudget.map((_, idx) => {
		if (idx >= fieldingDelay) {
			// return applyOsEscalation(data, arrQuantity, idx, fieldingDelay) * applyInflation(data, idx);
			// apply -1 * to the final value per Kyle's request - 8/15/2025
			return applyOsEscalation(data, arrQuantity, idx, fieldingDelay) * applyInflation(data, idx) * -1;
		}
		return 0;
	});

	// Recalculate rolling total inventory for each period
	let tmpRollingTotal = 0;
	arrRollingTotal = arrRollingTotal.map((_, idx) => {
		if (idx === 0) tmpRollingTotal = data.total_existing_inventory;
		tmpRollingTotal -= (data.item_planned_retirement[idx] || 0) + (arrQuantity[idx] || 0);
		return tmpRollingTotal;
	});

	// Update data object with results
	data.item_change_in_budget = [...arrBudget];
	data.item_change_in_quantity = [...arrQuantity];
	data.item_rolling_total = [...arrRollingTotal];
	data.item_change_in_budget_total = arrBudget.reduce((a, b) => a + b, 0);
	data.item_change_in_quantity_total = arrQuantity.reduce((a, b) => a + b, 0);

	// ----------------------------------------------------------
	// OPTIONAL DEBUG BLOCK (specific to item id 485)
	// ----------------------------------------------------------
	if (data.id === 485) {
		debugLog(4, "[scenarioUtils][PROCESS] applyRetireMoreLess", {
			item_1: data,
			arraBudget: arrBudget,
			arrQuantity: arrQuantity,
			arrRollingTotal: arrRollingTotal,
			option: option,
		});
	}

	return data;
};

/**
 * ============================================================
 * DELAY — BUSINESS RULES
 * These rules describe EXACTLY what this function implements,
 * matching both the Excel Delay model and the RAVEN logic.
 * ============================================================
 *
 * 1. When Delay is selected, the baseline budget and baseline
 *    quantity arrays are shifted one year LATER (to the RIGHT).
 *    The FIRST year after the shift is set to zero.
 *
 *    Example:
 *      FY24 ← 0
 *      FY25 ← FY24
 *      FY26 ← FY25
 *      FY27 ← FY26
 *      FY28 ← FY27
 *
 *    This matches the “push the program back one year” behavior
 *    described in the Excel Delay tab.
 *
 * ------------------------------------------------------------
 * 2. Change in Quantity (ΔQty):
 *
 *    The following rules match the Excel Delay formulas:
 *
 *    • In the delay year (the year whose schedule is pushed out),
 *        ΔQty = –BaselineQty[delay year]
 *
 *      Meaning: the originally planned quantity in the delay year
 *      is removed because the program is pushed back.
 *
 *    • For all years AFTER the delay year:
 *        ΔQty[t] = BaselineQty[t−1] – BaselineQty[t]
 *
 *      This represents the difference created by shifting the
 *      baseline one year to the right.
 *
 *    • All years BEFORE the delay year:
 *        ΔQty = 0
 *
 * ------------------------------------------------------------
 * 3. Change in Budget (ΔBudget) baseline component:
 *
 *    These rules mirror ΔQty, but for budget:
 *
 *    • In the delay year:
 *        ΔBudget = –BaselineBudget[delay year]
 *
 *    • For all years AFTER the delay year:
 *        ΔBudget[t] = BaselineBudget[t−1] – BaselineBudget[t]
 *
 *    • All years BEFORE the delay year:
 *        ΔBudget = 0
 *
 * ------------------------------------------------------------
 * 4. O&S + Inflation cost component:
 *
 *    • O&S applies only after "fielding delay" years have passed.
 *
 *    • For a given year t:
 *        sumApplicableQty = sum of ΔQty from all years
 *                           that are at least `fieldingDelay`
 *                           years old.
 *
 *    • O&S core formula:
 *        O&S(t) = (sumApplicableQty / increment size)
 *                × O&S unit cost
 *
 *    • Inflation / escalation multiplier:
 *        multiplier(t) = cumulative product of
 *                        (1 + inflation[i] + escalation[i])
 *        from year 1 through year t.
 *
 *    • Final budget delta for the year:
 *        ΔBudget[t] = baseline ΔBudget[t]
 *                     + (O&S(t) × multiplier(t))
 *
 * ------------------------------------------------------------
 * 5. Rolling Inventory:
 *
 *    Exactly like the Excel inventory calculation:
 *
 *        Inventory[0] = ExistingInventory
 *                       + BaselineQty[0]
 *                       + ΔQty[0]
 *
 *        Inventory[t] = Inventory[t−1]
 *                        + BaselineQty[t]
 *                        + ΔQty[t]
 *
 * ------------------------------------------------------------
 * 6. Zero-Option Behavior:
 *
 *    When option.value === 0:
 *    • All ΔQty values are zero.
 *    • All ΔBudget values are zero.
 *    • Rolling inventory becomes purely baseline-driven.
 *
 * ------------------------------------------------------------
 *
 * Apply a "Delay" option to a data item, shifting budgets/
 * quantities right and recalculating deltas and inventory totals.
 *
 * @param {Object} data - The item object containing budget,
 *                        quantity, baseline arrays, inflation,
 *                        escalation, O&S cost params, etc.
 * @param {Object} option - The option config:
 *                          { comp_type_id, index, value }.
 * @returns {Object} The mutated data object with updated deltas
 *                   and rolling totals.
 *
 * @example
 * applyDelay(item, { comp_type_id: 401, index: 2, value: 1 });
 */
export const applyDelay = (data, option) => {
	// Parameter safety checks
	if (
		!data ||
		!option ||
		typeof option.index !== "number" ||
		typeof option.value !== "number" ||
		!Array.isArray(data.item_change_in_budget) ||
		!Array.isArray(data.item_change_in_quantity) ||
		!Array.isArray(data.item_rolling_total) ||
		!Array.isArray(data.item_baseline_budget) ||
		!Array.isArray(data.item_baseline_quantity) ||
		!Array.isArray(data.bl_periods)
	) {
		console.warn("applyDelay: Invalid parameters", { data, option });
		return data || {};
	}

	// Field Delay from spreadsheet (Delay tab → Item Values → Fielding Delay)
	// Although not used for shifting schedule, it controls when O&S + inflation begin.
	const fieldingDelay = data.fielding_delay ? parseInt(data.fielding_delay) : 0;

	// Working arrays initialized from *existing deltas* (the deltas already stored in DB)
	// These will be overwritten when option.value === 1 (delay)
	let arrBudget = [...data.item_change_in_budget];
	let arrQuantity = [...data.item_change_in_quantity];
	let arrRollingTotal = [...data.item_rolling_total];

	if (option.value === 0) {
		// ----------------------------------------------------------
		// CASE 1: option.value === 0 → NO DELAY
		// Spreadsheet: all Change in Budget & Change in Quantity = 0
		// ----------------------------------------------------------

		// Zero out all changes
		arrBudget = new Array(data.bl_periods.length).fill(0);
		arrQuantity = new Array(data.bl_periods.length).fill(0);
	} else if (option.value === 1) {
		// ----------------------------------------------------------
		// CASE 2: option.value === 1 → APPLY DELAY
		// ---------------------------------------------------------

		// Shift baseline arrays and recalculate
		// ------------------------------------------------------
		// LOAD BASELINE ARRAYS (Excel: baseline rows)
		// ------------------------------------------------------
		arrBudget = [...data.item_baseline_budget];
		arrQuantity = [...data.item_baseline_quantity];

		// ------------------------------------------------------
		// SHIFT BASELINE RIGHT BY 1 PERIOD (Delay spreadsheet)
		//
		// Example:
		//   FY24 ← 0
		//   FY25 ← FY24
		//   FY26 ← FY25
		//   FY27 ← FY26
		//   FY28 ← FY27
		//
		// Implemented by shiftArrayRight()
		// ------------------------------------------------------
		arrBudget = shiftArrayRight(arrBudget, option.index);
		arrQuantity = shiftArrayRight(arrQuantity, option.index);

		// ------------------------------------------------------
		// ΔQTY — CHANGE IN QUANTITY (Excel Delay logic)
		// ------------------------------------------------------
		// Adjust quantities: zero at delay, differences at later periods
		arrQuantity = arrQuantity.map((x, idx) => {
			// Delay year: negative of baseline (program cannot execute)
			if (idx === option.index) return 0 - data.item_baseline_quantity[idx];

			// Years after delay: difference between adjacent baseline years
			// Excel: Baseline[t−1] − Baseline[t]
			if (idx > option.index && idx < arrBudget.length)
				return data.item_baseline_quantity[idx - 1] - data.item_baseline_quantity[idx];

			// Years before delay: return shifted baseline (effectively Δ=0)
			return x;
		});

		// ------------------------------------------------------
		// ΔBUDGET — CHANGE IN BUDGET (Excel Delay logic)
		// Mirrors ΔQty rules but using baseline budget.
		// ------------------------------------------------------
		// Adjust budgets: zero at delay, differences at later periods
		arrBudget = arrBudget.map((x, idx) => {
			// Years after delay: baseline difference
			if (idx > option.index && idx < arrQuantity.length)
				return data.item_baseline_budget[idx - 1] - data.item_baseline_budget[idx];

			// Delay year: remove baseline budget
			if (idx === option.index) return 0 - data.item_baseline_budget[idx];

			// Years before delay: unchanged baseline shift
			return x;
		});

		// ------------------------------------------------------
		// APPLY O&S ESCALATION + INFLATION
		// Excel: only applies after fieldingDelay years have passed.
		//
		// O&S(t) = (sum ΔQty contributing) / increment × O&S unit cost
		// Total(t) = ΔBudget[t] + O&S(t) × inflationMultiplier[t]
		// ------------------------------------------------------
		arrBudget = arrBudget.map((x, idx) => {
			if (idx >= fieldingDelay) {
				x += applyOsEscalation(data, arrQuantity, idx, fieldingDelay) * applyInflation(data, idx);
				// x=applyOsEscalation(data, arrQuantity, idx, fieldingDelay);
			}
			return x;
		});
	}

	// ----------------------------------------------------------
	// ROLLING INVENTORY TOTAL (Excel cumulative inventory logic)
	//
	// Inventory[t] = Inventory[t−1] + BaselineQty[t] + ΔQty[t]
	// ----------------------------------------------------------
	let tmpRollingTotal = 0;
	arrRollingTotal = arrRollingTotal.map((x, idx) => {
		if (idx === 0) tmpRollingTotal = data.total_existing_inventory;
		tmpRollingTotal += data.item_baseline_quantity[idx] + arrQuantity[idx];
		return tmpRollingTotal;
	});

	// Update data object with results
	data.item_change_in_budget = [...arrBudget];
	data.item_change_in_quantity = [...arrQuantity];
	data.item_rolling_total = [...arrRollingTotal];
	data.item_change_in_budget_total = arrBudget.reduce((a, b) => a + b, 0);
	data.item_change_in_quantity_total = arrQuantity.reduce((a, b) => a + b, 0);

	// ----------------------------------------------------------
	// OPTIONAL DEBUG BLOCK (specific to item id 482)
	// ----------------------------------------------------------
	if (data.id === 482) {
		debugLog(4, "[scenarioUtils][PROCESS] applyDelay", {
			item_1: data,
			arraBudget: arrBudget,
			arrQuantity: arrQuantity,
			arrRollingTotal: arrRollingTotal,
			option: option,
		});
	}

	return data;
};

/**
 * ============================================================
 * ACCELERATE — BUSINESS RULES
 * These rules describe EXACTLY what this function implements,
 * matching both the Excel Accelerate model and RAVEN logic.
 * ============================================================
 *
 * 1. When Accelerate is selected, the baseline budget and
 *    baseline quantity arrays are shifted one year earlier.
 *    The final year after the shift is set to zero.
 *
 * 2. Change in Quantity (ΔQty):
 *    • In the accelerate year, ΔQty comes from next year's baseline.
 *    • In later years (before the last year), ΔQty is the difference
 *      between next year's baseline quantity and this year's baseline.
 *    • In the final year, ΔQty is always zero.
 *
 * 3. Change in Budget (ΔBudget) baseline component:
 *    • In the accelerate year, ΔBudget comes from next year's baseline
 *      (via the shift).
 *    • In later years (before the last year), ΔBudget is the difference
 *      between next year's baseline budget and this year's baseline.
 *    • In the final year, ΔBudget is always zero.
 *
 * 4. O&S + Inflation cost component:
 *    • O&S applies only after "fielding delay" years have passed.
 *    • For a given year t, all quantity changes from earlier years
 *      that are at least "fielding delay" years old contribute to O&S.
 *    • O&S cost is: (sum of applicable ΔQty / increment size) × O&S unit cost.
 *    • This O&S cost is multiplied by cumulative inflation/escalation
 *      factors from the accelerate year through the current year.
 *    • This O&S component is added to the baseline ΔBudget for that year.
 *
 * 5. Rolling Inventory:
 *    • Each year's inventory = previous inventory + baseline quantity
 *      + ΔQty for that year.
 *
 * ============================================================
 *
 * Apply an "Accelerate" option to a data item, shifting budgets/quantities left and recalculating totals.
 * If value is 0: all change arrays are set to zero.
 * If value is 1: baseline arrays are shifted left, then budget/quantity deltas and rolling totals are recalculated.
 *
 * @param {Object} data - The item object, including budget, quantity, and baseline arrays.
 * @param {Object} option - The option config, e.g. { comp_type_id, index, value }.
 * @returns {Object} The mutated data object with change arrays and totals updated.
 *
 * @example
 * applyAccelerate(item, { comp_type_id: 402, index: 1, value: 1 });
 */
export const applyAccelerate = (data, option) => {
	// Parameter safety checks
	if (
		!data ||
		!option ||
		typeof option.index !== "number" ||
		typeof option.value !== "number" ||
		!Array.isArray(data.item_change_in_budget) ||
		!Array.isArray(data.item_change_in_quantity) ||
		!Array.isArray(data.item_rolling_total) ||
		!Array.isArray(data.item_baseline_budget) ||
		!Array.isArray(data.item_baseline_quantity) ||
		!Array.isArray(data.bl_periods)
	) {
		console.warn("applyAccelerate: Invalid parameters", { data, option });
		return data || {};
	}

	// Field Delay from spreadsheet (Accelerate tab → Item Values → Fielding Delay)
	// Although not used for shifting schedule, it controls when O&S + inflation begin.
	const fieldingDelay = data.fielding_delay ? parseInt(data.fielding_delay) : 0;

	// Working arrays initialized from *existing deltas* (the deltas already stored in DB)
	// These will be overwritten when option.value === 1 (accelerate)
	let arrBudget = [...data.item_change_in_budget];
	let arrQuantity = [...data.item_change_in_quantity];
	let arrRollingTotal = [...data.item_rolling_total];

	if (option.value === 0) {
		// ----------------------------------------------------------
		// CASE 1: option.value === 0  → no accelerate
		//
		// Spreadsheet behavior: if no accelerate, all Change in Budget & Quantity = 0.
		// ----------------------------------------------------------

		// Zero out all changes
		arrBudget = new Array(data.bl_periods.length).fill(0);
		arrQuantity = new Array(data.bl_periods.length).fill(0);
	} else if (option.value === 1) {
		// ----------------------------------------------------------
		// CASE 2: option.value === 1  → APPLY ACCELERATE
		// ----------------------------------------------------------

		// ------------------------------------------------------
		// LOAD BASELINE arrays exactly like the spreadsheet uses:
		// Baseline Budget:  Row 14 in spreadsheet
		// Baseline Quantity: Row 15 in spreadsheet
		//
		// Shift arrays left for acceleration effect
		// ------------------------------------------------------
		arrBudget = [...data.item_baseline_budget];
		arrQuantity = [...data.item_baseline_quantity];

		// ------------------------------------------------------
		// SHIFT BASELINE LEFT by 1 period
		// (matches Accelerate spreadsheet behavior)
		//
		// Example:
		//  FY25  ← FY26
		//  FY26  ← FY27
		//  FY27  ← FY28
		//  FY28  ← 0
		//
		// Excel equivalent: "Baseline (Shifted)" rows you described.
		// ------------------------------------------------------
		arrBudget = shiftAndZeroLeadingLeft(arrBudget, option.index);
		arrQuantity = shiftAndZeroLeadingLeft(arrQuantity, option.index);

		// ------------------------------------------------------
		// COMPUTE CHANGE IN QUANTITY (spreadsheet Row 18 logic)
		// ------------------------------------------------------
		arrQuantity = arrQuantity.map((x, idx) => {
			// Excel: FY(X) = BaselineQty(X+1)
			if (idx === option.index) return data.item_baseline_quantity[idx + 1];

			// Excel: FY(X+1) = BaselineQty(X+2) - BaselineQty(X+1)
			//        FY(X+2) = BaselineQty(X+3) - BaselineQty(X+2)
			if (idx > option.index && idx < arrQuantity.length - 1)
				return data.item_baseline_quantity[idx + 1] - data.item_baseline_quantity[idx];

			// Excel: Last year = 0
			if (idx === arrQuantity.length - 1) return 0;

			// Default: return shifted baseline quantity
			return x;
		});

		// ------------------------------------------------------
		// COMPUTE CHANGE IN BUDGET (spreadsheet Row 17 logic)
		// Calculate budget changes based on shifted baseline
		// ------------------------------------------------------
		arrBudget = arrBudget.map((x, idx) => {
			// Excel FY(X+1), FY(X+2): (next baseline) - (current baseline)
			// FY26: = E14 − D14
			// FY27: = F14 − E14
			if (idx > option.index && idx < arrBudget.length - 1)
				return data.item_baseline_budget[idx + 1] - data.item_baseline_budget[idx];

			// Excel: last year change always = 0
			if (idx === arrBudget.length - 1) return 0;

			// Excel FY(X): receives baseline[X+1] automatically due to shift
			return x;
		});

		// ------------------------------------------------------
		// APPLY O&S ESCALATION + INFLATION (matches Excel multipliers)
		//
		// Spreadsheet:
		// FY26: O&S(FY25 qty) * (1+Infl25+Esc25)*(1+Infl26+Esc26)
		// FY27: O&S(FY26 qty) * same multipliers + FY27 multiplier
		// FY28: O&S(FY25 + FY26 qty) * four-year multiplier chain
		//
		// Code applies EXACTLY the same logic.
		//
		// Apply escalation and inflation after fielding delay
		// ------------------------------------------------------
		arrBudget = arrBudget.map((x, idx) => {
			if (idx >= fieldingDelay) {
				x += applyOsEscalation(data, arrQuantity, idx, fieldingDelay) * applyInflation(data, idx);
			}
			return x;
		});
	}

	// ----------------------------------------------------------
	// UPDATE ROLLING TOTALS (inventory)
	//
	// Excel didn't show this, but RAVEN requires:
	// Inventory[idx] = PreviousInventory + BaselineQty + ChangeQty
	// ----------------------------------------------------------
	let tmpRollingTotal = 0;
	arrRollingTotal = arrRollingTotal.map((x, idx) => {
		if (idx === 0) tmpRollingTotal = data.total_existing_inventory;
		tmpRollingTotal += data.item_baseline_quantity[idx] + arrQuantity[idx];
		return tmpRollingTotal;
	});

	// Update data object with results
	data.item_change_in_budget = [...arrBudget];
	data.item_change_in_quantity = [...arrQuantity];
	data.item_rolling_total = [...arrRollingTotal];
	data.item_change_in_budget_total = arrBudget.reduce((a, b) => a + b, 0);
	data.item_change_in_quantity_total = arrQuantity.reduce((a, b) => a + b, 0);

	// ----------------------------------------------------------
	// OPTIONAL DEBUG BLOCK (specific to item id 953)
	// ----------------------------------------------------------
	if (data.id === 953) {
		debugLog(4, "[scenarioUtils][PROCESS] applyAccelerate", {
			item_1: data,
			arraBudget: arrBudget,
			arrQuantity: arrQuantity,
			arrRollingTotal: arrRollingTotal,
			option: option,
		});
	}

	return data;
};

/**
 * Applies a "Terminate" operation to a data item, recalculating budget and quantity changes.
 *
 * When `option.value` is 0, restores previous change arrays (undo).
 * When `option.value` is 1, sets future periods' changes to negative baseline (terminate), and recalculates rolling totals and budget.
 *
 * @param {Object} data - The item data object to update.
 * @param {Object} option - Terminate option, e.g. { comp_type_id, index, value }.
 * @returns {Object} The updated data object with recalculated change arrays and totals.
 *
 * @example
 * applyTerminate(item, { comp_type_id: 403, index: 3, value: 1 });
 */
export const applyTerminate = (data, option) => {
	// Parameter safety checks
	if (
		!data ||
		!option ||
		typeof option.index !== "number" ||
		typeof option.value !== "number" ||
		!Array.isArray(data.item_change_in_budget) ||
		!Array.isArray(data.item_change_in_quantity) ||
		!Array.isArray(data.item_rolling_total) ||
		!Array.isArray(data.item_baseline_budget) ||
		!Array.isArray(data.item_baseline_quantity)
	) {
		console.warn("applyTerminate: Invalid parameters", { data, option });
		return data || {};
	}

	const fieldingDelay = data.fielding_delay ? parseInt(data.fielding_delay) : 0;

	let arrBudget = [...data.item_change_in_budget];
	// let arrBudget = [...data.item_baseline_budget];
	let arrQuantity = [...data.item_change_in_quantity];
	let arrRollingTotal = [...data.item_rolling_total];

	if (option.value === 0) {
		// Restore previous arrays if available (undo termination)
		if (typeof data.item_change_in_budget_prev !== "undefined") {
			arrBudget = [...data.item_change_in_budget_prev];
			arrQuantity = [...data.item_change_in_quantity_prev];
			arrRollingTotal = [...data.item_rolling_total_prev];
		}
	} else if (option.value === 1) {
		// Store previous state before applying terminate
		data.item_change_in_budget_prev = [...arrBudget];
		data.item_change_in_quantity_prev = [...arrQuantity];
		data.item_rolling_total_prev = [...arrRollingTotal];

		// Set quantities and budgets for periods >= option.index to negative baseline (terminated)
		arrQuantity = arrQuantity.map((x, idx) =>
			idx >= option.index ? data.item_baseline_quantity[idx] * -1 : x,
		);

		arrRollingTotal = arrRollingTotal.map((x, idx) => {
			// added on 4/9/2025; otherwise, the inventory rolling total will be NaN
			if (option.index === 0) return 0;

			if (idx >= option.index) return arrRollingTotal[option.index - 1];
			return x;
		});

		arrBudget = arrBudget.map((x, idx) =>
			idx >= option.index ? data.item_baseline_budget[idx] * -1 : x,
		);

		let pos = findLastNonZeroIndex(data.item_change_in_quantity);

		// Recalculate budget, applying escalation and inflation where applicable
		if (pos === -1) {
			arrBudget = arrBudget.map((x, idx) => {
				if (idx >= fieldingDelay) {
					x += applyOsEscalation(data, arrQuantity, idx, fieldingDelay) * applyInflation(data, idx);
					// x=applyOsEscalation(data, arrQuantity, idx, fieldingDelay);
				}
				return x;
			});
		} else {
			arrBudget = arrBudget.map((x, idx) => {
				if (idx >= fieldingDelay && idx >= option.index) {
					x += applyOsEscalation(data, arrQuantity, idx, fieldingDelay) * applyInflation(data, idx);
					// x=applyInflation(data, idx);
				}
				return x;
			});
		}
	}

	// Update data object with results
	data.item_change_in_budget = [...arrBudget];
	data.item_change_in_quantity = [...arrQuantity];
	data.item_rolling_total = [...arrRollingTotal];
	data.item_change_in_budget_total = arrBudget.reduce((a, b) => a + b, 0);
	data.item_change_in_quantity_total = arrQuantity.reduce((a, b) => a + b, 0);

	// ----------------------------------------------------------
	// OPTIONAL DEBUG BLOCK (specific to item id 921)
	// ----------------------------------------------------------
	if (data.id === 921) {
		debugLog(4, "[scenarioUtils][PROCESS] applyTerminate", {
			item_1: data,
			arraBudget: arrBudget,
			arrQuantity: arrQuantity,
			arrRollingTotal: arrRollingTotal,
			option: option,
		});
	}

	return data;
};

/**
 * Calculates and updates the new development program’s budget and quantity change arrays,
 * applying fielding delay, O&S escalation, and inflation. Updates totals and rolling totals on the data object.
 *
 * @param {Object} data - The item data object containing arrays and config fields.
 * @param {Object} option - Option object with { index, value } fields.
 * @param {number} option.index - The period index where the option is applied.
 * @param {number} option.value - 0 to clear all values; 1 to apply new development logic.
 * @returns {Object} The updated item data object.
 *
 * @example
 * applyNewDevelop(item, { index: 2, value: 1 });
 */
export const applyNewDevelop = (data, option) => {
	// Parameter safety checks
	if (
		!data ||
		!option ||
		typeof option.index !== "number" ||
		typeof option.value !== "number" ||
		!Array.isArray(data.bl_periods) ||
		!Array.isArray(data.item_rolling_total) ||
		!Array.isArray(data.item_new_budget) ||
		!Array.isArray(data.item_new_quantity)
	) {
		console.warn("applyNewDevelop: Invalid parameters", { data, option });
		return data || {};
	}

	const fieldingDelay = data.fielding_delay ? parseInt(data.fielding_delay) : 0;

	let arrBudget, arrQuantity;
	let arrRollingTotal = Array.isArray(data.item_rolling_total) ? [...data.item_rolling_total] : [];

	if (option.value === 0) {
		// Reset arrays to zeros for all periods
		// default values should be array of 0s
		arrBudget = new Array(data.bl_periods.length).fill(0);
		arrQuantity = new Array(data.bl_periods.length).fill(0);
	} else if (option.value === 1) {
		// Use the planned new budget/quantity and shift for new start period
		arrBudget = Array.isArray(data.item_new_budget) ? [...data.item_new_budget] : [];
		arrQuantity = Array.isArray(data.item_new_quantity) ? [...data.item_new_quantity] : [];
		arrBudget = shiftArrayRight(arrBudget, option.index);
		arrQuantity = shiftArrayRight(arrQuantity, option.index);

		arrBudget = arrBudget.map((x, idx) => {
			if (idx >= fieldingDelay) {
				x += applyOsEscalation(data, arrQuantity, idx, fieldingDelay) * applyInflation(data, idx);
				// x=applyOsEscalation(data, arrQuantity, idx, fieldingDelay);
			}
			return x;
		});
	}

	// Calculate rolling total: running sum of planned inventory
	let tmpRollingTotal = 0;
	arrRollingTotal = arrRollingTotal.map((x, idx) => {
		if (idx === 0) tmpRollingTotal = data.total_existing_inventory || 0;
		tmpRollingTotal += arrQuantity[idx] || 0;
		return tmpRollingTotal;
	});

	// Update data object with results
	data.item_change_in_budget = [...arrBudget];
	data.item_change_in_quantity = [...arrQuantity];
	data.item_rolling_total = [...arrRollingTotal];
	data.item_change_in_budget_total = arrBudget.reduce((a, b) => a + b, 0);
	data.item_change_in_quantity_total = arrQuantity.reduce((a, b) => a + b, 0);

	// ----------------------------------------------------------
	// OPTIONAL DEBUG BLOCK (specific to item id 481)
	// ----------------------------------------------------------
	if (data.id === 481) {
		debugLog(4, "[scenarioUtils][PROCESS] applyNewDevelop", {
			item: data,
			arraBudget: arrBudget,
			arrQuantity: arrQuantity,
			arrRollingTotal: arrRollingTotal,
			option: option,
		});
	}

	return data;
};

/**
 * Calculates the compounded inflation and O&S cost escalation factor up to the given period.
 *
 * @param {Object} data - The data object containing inflation and escalation arrays.
 * @param {number} pos - The end period index (inclusive) for compounding.
 * @returns {number} The compounded inflation/escalation multiplier.
 *
 * @example
 * // With bl_inflation_assumptions = [0, 2, 2.5], bl_os_cost_escalation = [0, 1, 1.5]
 * applyInflation(data, 2); // Calculates compound for periods 1 and 2
 */
export const applyInflation = (data, pos) => {
	let val = 1;
	const inflationArr = Array.isArray(data.bl_inflation_assumptions) ? data.bl_inflation_assumptions : [];
	const escalationArr = Array.isArray(data.bl_os_cost_escalation) ? data.bl_os_cost_escalation : [];

	for (let i = 1; i <= pos; i++) {
		const infl = data.bl_inflation_assumptions[i] ?? 0;
		const osc = data.bl_os_cost_escalation[i] ?? 0;
		val *= 1 + infl / 100 + osc / 100;
	}
	return val;
};

/**
 * Calculates the total O&S (Operations & Support) escalation cost for an item,
 * based on quantity changes up to the specified period, considering a fielding delay.
 *
 * @param {Object} data - Data object containing increment and os_cost_per_unit.
 * @param {number[]} arrQty - Array of quantity changes per period.
 * @param {number} pos - The period index to calculate up to.
 * @param {number} fieldingDelay - The number of periods to delay before counting quantities.
 * @returns {number} The O&S escalation cost for the given period.
 *
 * @example
 * // For arrQty = [1, 2, 3, 4], pos = 3, fieldingDelay = 1,
 * // sums arrQty[0] + arrQty[1] + arrQty[2], then scales.
 * applyOsEscalation(data, [1,2,3,4], 3, 1);
 */
export const applyOsEscalation = (data, arrQty, pos, fieldingDelay) => {
	let sumQty = 0;
	const limit = Math.max(0, pos - fieldingDelay);
	for (let i = 0; i <= limit; i++) {
		sumQty += arrQty[i] || 0;
	}
	const increment = data.increment || 1;
	const costPerUnit = data.os_cost_per_unit || 0;
	return (sumQty / increment) * costPerUnit;
};

export const precisionRound = (number, precision) => {
	var factor = Math.pow(10, precision);
	var n = precision < 0 ? number : 0.01 / factor + number;
	return Math.round(n * factor) / factor;
};

const idWithBudget = [111, 112, 113];
const groupWithBudget = [
	{ id: 111, column: "military_service_id", code: "military_service" },
	{ id: 112, column: "domain_id", code: "domain" },
	{ id: 111, column: "category_id", code: "category" },
];

/**
 * Prepares and enriches a scenario object for downstream consumption (UI or data logic).
 * - Deep clones the scenario to prevent mutation of the original.
 * - Attaches sorted/augmented config arrays as cfg_* properties.
 * - Flattens top-level baseline config fields to the root for convenience.
 * - Populates scenario access control and item-level calculation fields.
 * - Enriches preference categories with config/value information.
 * - Applies budget/calculation logic to each baseline item.
 *
 * @param {Object} scenario - The scenario object, including baseline, configs, items, and scenario_item_configs.
 * @returns {Object} The enriched scenario object, ready for use in the app.
 *
 * @example
 * const readyScenario = prepareScenarioObject(rawScenario);
 * // readyScenario.cfg_FORCE, readyScenario.baseline_items[0].option_type_name, etc. now available
 */
export const prepareScenarioObject = (scenario) => {
	if (!scenario || typeof scenario !== "object") return {};

	// creates a shallow copy of the top-level object (baseline)
	// Note: It does not deeply clone any of the nested arrays or objects—such as data.baseline_accesses or data.baseline_configs. Those arrays are still references to the original arrays in your Redux store or wherever the original baseline came from
	// const data = { ...baseline };

	// Deep clone to avoid mutating originals
	const data = deepClone(scenario);
	const lookups = JSON.parse(sessionStorage.getItem("lookups")) || {};

	// Attach sorted/augmented config arrays
	// lookups.BL_CFG_TYPES.forEach((c) => {
	(lookups.BL_CFG_TYPES || []).forEach((c) => {
		const arrayTemp = filterObjectsByProperty(data.baseline.baseline_configs, "comp_type_id", c.id);
		let sortedArray = arrayTemp.sort((x, y) => x.sort_id - y.sort_id);
		if (c.format === "currency") {
			sortedArray = sortedArray.map((x) => ({
				...x,
				currency_unit: data.currency_unit,
			}));
		}
		// Add placeholder for change_in_budget if needed
		if (typeof idWithBudget !== "undefined" && idWithBudget.indexOf(c.id) !== -1) {
			sortedArray.forEach((y) => {
				y.change_in_budget = [];
			});
		}
		data.baseline[`cfg_${c.code}`] = sortedArray;
		data[`cfg_${c.code}`] = sortedArray;
	});

	// Copy top-level baseline config fields to root for easier access
	Object.assign(data, {
		baseline_configs: data.baseline.baseline_configs,
		baseline_items: data.baseline.baseline_items,
		currency_code: data.baseline.currency_code,
		currency_display_decimal: data.baseline.currency_display_decimal,
		currency_id: data.baseline.currency_id,
		currency_symbol: data.baseline.currency_symbol,
		currency_unit: data.baseline.currency_unit,
	});

	// Lookup and prepare preference categories
	data.BL_LOOKUPS = filterObjectsByProperty(lookups.BL_LOOKUPS_ALL, "flag2", 10).sort(
		(a, b) => a.sort_id - b.sort_id,
	);
	data.prefCategory = filterObjectsByProperty(data.BL_LOOKUPS, "type_id", 2);
	data.prefCategory.forEach((x) => {
		let arrayItems = [];
		if (x.id === 601) {
			// Force Balance
			arrayItems = filterObjectsByProperty(data.BL_LOOKUPS, "type_id", 21);
		} else if (x.id === 611) {
			// Budget Preference
			arrayItems = filterObjectsByProperty(data.baseline.baseline_configs, "comp_type_id", 11).sort(
				(a, b) => a.sort_id - b.sort_id,
			);
			x.arrayCfgs = filterObjectsByProperty(data.BL_LOOKUPS, "group_id", 100).filter(
				(y) => y.type_id === 1,
			);

			arrayItems.forEach((y) => {
				x.arrayCfgs.forEach((z) => {
					const match = findObjectByProperty(
						filterObjectsByProperty(data.baseline.baseline_configs, "comp_type_id", z.id),
						"sort_id",
						y.sort_id,
					);
					y[`${z.code}`] = match ? match.comp_value : undefined;
				});
			});
		} else {
			// Operational Preferences and Geographic Preferences
			arrayItems = filterObjectsByProperty(data.baseline.baseline_configs, "comp_type_id", x.id).sort(
				(a, b) => a.sort_id - b.sort_id,
			);
		}
		x.items = arrayItems;
	});

	// Prepare scenario accesses
	data.scenario_accesses.forEach((x) => {
		x.update_flag = 0;
	});
	lookups.BL_CFG_ACCESSES.forEach((c) => {
		const arrayTemp = filterObjectsByProperty(data.scenario_accesses, "comp_type_id", c.id);
		let sortedArray = arrayTemp.sort((x, y) => x.sort_id - y.sort_id);
		data[`cfg_${c.code}`] = sortedArray;
	});

	// Scenario calculations should only run on Baseline Items that are active and
	// fully ready, so selection-only filtering and budget math stay in sync.
	const readyActiveItemIds = new Set(
		(Array.isArray(data.baseline_items) ? data.baseline_items : [])
			.filter((item) => Number(item?.status_id) === 1 && Number(item?.data_status_id) === 1)
			.map((item) => Number(item.id)),
	);
	data.baseline_items = (Array.isArray(data.baseline_items) ? data.baseline_items : []).filter((item) =>
		readyActiveItemIds.has(Number(item?.id))
	);
	data.scenario_item_configs = (Array.isArray(data.scenario_item_configs)
		? data.scenario_item_configs
		: []
	).filter((cfg) => readyActiveItemIds.has(Number(cfg?.item_id)));

	// Prepare per-item budgets and calculated fields
	data.baseline_items = data.baseline_items.map((item) => {
		let x = deepClone(item); // or { ...item }, depending on needs

		x.scenario_item_configs = filterObjectsByProperty(data.scenario_item_configs, "item_id", x.id);

		// ------------------------------------------------------------
		// Ensure scenario_item_configs completeness (moved from UI)
		// ------------------------------------------------------------

		// Find option type definition for this item
		const optType = findObjectByProperty(lookups.BL_ITEM_OPT_TYPES, "id", x.option_type_id);

		// UI getSelectionOptions expects configs per (sort_id × comp_type)
		if (optType && Array.isArray(optType.comp_types)) {
			// Collect all existing configs by sort_id
			const cfgsBySort = {};
			x.scenario_item_configs.forEach((cfg) => {
				if (!cfgsBySort[cfg.sort_id]) {
					cfgsBySort[cfg.sort_id] = [];
				}
				cfgsBySort[cfg.sort_id].push(cfg);
			});

			// Iterate baseline periods (same source UI relies on)
			x.bl_periods = getValuesByPropertyPath(
				filterObjectsByProperty(data.baseline_configs, "comp_type_id", 11),
				"comp_value",
			);

			x.bl_periods.forEach((_, sortIdx) => {
				const sortId = sortIdx + 1; // UI logic uses 1-based sort_id

				if (!cfgsBySort[sortId]) cfgsBySort[sortId] = [];

				optType.comp_types.forEach((ct) => {
					const exists = findObjectByProperty(cfgsBySort[sortId], "comp_type_id", ct.id);

					if (!exists) {
						const newCfg = {
							id: null, // placeholder until persisted
							baseline_id: x.baseline_id,
							scenario_id: data.id,
							item_id: x.id,
							comp_type_id: ct.id,
							comp_value1: "0",
							comp_value2: null,
							sort_id: sortId,
							status_id: 1,
							update_flag: 101, // SAME semantic as UI-created
						};

						x.scenario_item_configs.push(newCfg);
						cfgsBySort[sortId].push(newCfg);
					}
				});
			});
		}

		x.budgetChange = [];
		x.quantityChange = [];
		x.budgetChangeTotal = 0;
		x.quantityChangeTotal = 0;

		// Planned and change calculations
		if (x.option_type_id === 304) {
			x.total_planned = getValuesByPropertyPath(
				filterObjectsByProperty(x.baseline_item_configs, "comp_type_id", 506),
				"comp_value",
			).reduce((a, b) => parseInt(a) + parseInt(b), 0);
			x.total_change = getValuesByPropertyPath(
				filterObjectsByProperty(x.scenario_item_configs, "comp_type_id", 406),
				"comp_value1",
			).reduce((a, b) => parseInt(a) + parseInt(b), 0);
		} else if (x.option_type_id === 302 || x.option_type_id === 303) {
			x.total_planned = getValuesByPropertyPath(
				filterObjectsByProperty(x.baseline_item_configs, "comp_type_id", 502),
				"comp_value",
			).reduce((a, b) => parseInt(a) + parseInt(b), 0);
			x.total_change = getValuesByPropertyPath(
				filterObjectsByProperty(x.scenario_item_configs, "comp_type_id", 404),
				"comp_value1",
			).reduce((a, b) => parseInt(a) + parseInt(b), 0);
		}

		// Attach option type name
		x.option_type_name = findObjectByProperty(lookups.BL_ITEM_OPT_TYPES, "id", x.option_type_id).name;

		// Attach all per-period arrays and other config fields
		x.bl_periods = getValuesByPropertyPath(
			filterObjectsByProperty(data.baseline_configs, "comp_type_id", 11),
			"comp_value",
		);
		x.bl_inflation_assumptions = getValuesByPropertyPath(
			filterObjectsByProperty(data.baseline_configs, "comp_type_id", 21),
			"comp_value",
		).map(Number);
		x.bl_os_cost_escalation = getValuesByPropertyPath(
			filterObjectsByProperty(data.baseline_configs, "comp_type_id", 22),
			"comp_value",
		).map(Number);

		x.item_baseline_budget = getValuesByPropertyPath(
			filterObjectsByProperty(x.baseline_item_configs, "comp_type_id", 501),
			"comp_value",
		).map(Number);
		x.item_baseline_quantity = getValuesByPropertyPath(
			filterObjectsByProperty(x.baseline_item_configs, "comp_type_id", 502),
			"comp_value",
		).map(Number);
		x.item_unit_cost = getValuesByPropertyPath(
			filterObjectsByProperty(x.baseline_item_configs, "comp_type_id", 503),
			"comp_value",
		).map(Number);
		x.item_max_increase = getValuesByPropertyPath(
			filterObjectsByProperty(x.baseline_item_configs, "comp_type_id", 504),
			"comp_value",
		).map(Number);
		x.item_max_decrease = getValuesByPropertyPath(
			filterObjectsByProperty(x.baseline_item_configs, "comp_type_id", 505),
			"comp_value",
		).map(Number);
		x.item_planned_retirement = getValuesByPropertyPath(
			filterObjectsByProperty(x.baseline_item_configs, "comp_type_id", 506),
			"comp_value",
		).map(Number);
		x.item_new_budget = getValuesByPropertyPath(
			filterObjectsByProperty(x.baseline_item_configs, "comp_type_id", 507),
			"comp_value",
		).map(Number);
		x.item_new_quantity = getValuesByPropertyPath(
			filterObjectsByProperty(x.baseline_item_configs, "comp_type_id", 508),
			"comp_value",
		).map(Number);

		// Ensure new_budget and new_quantity arrays are filled for all periods
		x.item_new_budget =
			x.item_new_budget.length > 0
				? x.item_new_budget
				: new Array(data.baseline.number_of_period).fill(0);
		x.item_new_quantity =
			x.item_new_quantity.length > 0
				? x.item_new_quantity
				: new Array(data.baseline.number_of_period).fill(0);

		x.item_change_in_budget = new Array(data.baseline.number_of_period).fill(0);
		x.item_change_in_quantity = new Array(data.baseline.number_of_period).fill(0);
		x.item_rolling_total = new Array(data.baseline.number_of_period).fill(0);

		x.currency_display_decimal = data.baseline.currency_display_decimal;
		x.currency_symbol = data.baseline.currency_symbol;
		x.currency_unit = data.baseline.currency_unit;

		// Per-option calculation triggers
		const arrayCfgDelay = filterObjectsByProperty(x.scenario_item_configs, "comp_type_id", 401);
		const arrayCfgAccelerate = filterObjectsByProperty(x.scenario_item_configs, "comp_type_id", 402);
		const arrayCfgTerminate = filterObjectsByProperty(x.scenario_item_configs, "comp_type_id", 403);
		const arrayCfgAddCut = filterObjectsByProperty(x.scenario_item_configs, "comp_type_id", 404);
		const arrayCfgRetireMoreLess = filterObjectsByProperty(x.scenario_item_configs, "comp_type_id", 406);
		const arrayCfgNewDevelop = filterObjectsByProperty(x.scenario_item_configs, "comp_type_id", 408);

		// prepare data for budget change calculation
		// Existing Development Program (301):  Accelerate(402), Delay(401), //Terminate(403)
		// Existing Procurement Program (302): Add/Cut(404), Cut(405 x), Terminate(403)
		// Existing Forces (303): Add/Cut(404), Cut(405 x)
		// Legacy System (304): Retire more/less(406), RETIRE LESS(407 x)
		// New Development Program (305): New develop(408)
		switch (x.option_type_id) {
			case 301:
				arrayCfgDelay.forEach((y, idx2) => {
					if (parseInt(y.comp_value1) === 1) {
						x = applyDelay(x, { comp_type_id: 401, index: idx2, value: 1 });
					}
				});
				arrayCfgAccelerate.forEach((y, idx2) => {
					if (parseInt(y.comp_value1) === 1) {
						x = applyAccelerate(x, { comp_type_id: 402, index: idx2, value: 1 });
					}
				});
				arrayCfgTerminate.forEach((y, idx2) => {
					if (parseInt(y.comp_value1) === 1) {
						x = applyTerminate(x, { comp_type_id: 403, index: idx2, value: 1 });
					}
				});
				break;
			case 302:
				arrayCfgAddCut.forEach((y, idx2) => {
					x = applyAddCut(x, {
						comp_type_id: 404,
						index: idx2,
						value: y.comp_value1 * 1,
					});
				});
				arrayCfgTerminate.forEach((y, idx2) => {
					if (parseInt(y.comp_value1) === 1) {
						x = applyTerminate(x, { comp_type_id: 403, index: idx2, value: 1 });
					}
				});
				break;
			case 303:
				arrayCfgAddCut.forEach((y, idx2) => {
					x = applyAddCut(x, {
						comp_type_id: 404,
						index: idx2,
						value: y.comp_value1 * 1,
					});
				});
				break;
			case 304:
				arrayCfgRetireMoreLess.forEach((y, idx2) => {
					x = applyRetireMoreLess(x, {
						comp_type_id: 406,
						index: idx2,
						value: y.comp_value1 * 1,
					});
				});
				break;
			case 305:
				arrayCfgNewDevelop.forEach((y, idx2) => {
					if (parseInt(y.comp_value1) === 1) {
						x = applyNewDevelop(x, { comp_type_id: 408, index: idx2, value: 1 });
					}
				});
				break;
			default:
				break;
		}

		return x; // THIS ensures the returned array holds updated values
	});

	// Prepare baseline total budget array
	data.baseline_total_budget = getValuesByPropertyPath(
		filterObjectsByProperty(data.baseline.baseline_configs, "comp_type_id", 23),
		"comp_value",
	).map(Number);

	data.statusLabel = data.status_id === 1 ? "Active" : data.status_id === 0 ? "Archived" : "Unknown";

	// Always recalculate budget and return final structure
	// data = getBudgetUpdate(data);
	// return data;
	return getBudgetUpdate(data);
};

/**
 * Calculates and updates total and per-group budget changes on a scenario/baseline data object.
 *
 * - Sums all `item_change_in_budget` arrays from `baseline_items` to produce `baseline_change_in_budget`.
 * - For each configured group (e.g., Military Services, Domains, Categories),
 *   sums `item_change_in_budget` for items belonging to that group and attaches the result to each group config as `change_in_budget`.
 *
 * @param {Object} data - The scenario or baseline data object. Must contain:
 *   - `baseline.number_of_period` {number}
 *   - `baseline_items` {Array<Object>} (each with `item_change_in_budget` and group properties)
 *   - `cfg_{code}` arrays for each group in `groupWithBudget`
 * @returns {Object} The same object, but with `baseline_change_in_budget` (array of total changes)
 *   and `change_in_budget` attached to each group config in `cfg_{code}` arrays.
 *
 * @example
 * const updatedData = getBudgetUpdate(data);
 * // updatedData.baseline_change_in_budget, updatedData.cfg_MIL.change_in_budget, etc.
 */
export const getBudgetUpdate = (data) => {
	// Initialize total budget change array
	let totalChange = new Array(data.baseline.number_of_period).fill(0);

	// Sum up item budget changes
	// data.baseline_items.map((x) => {
	// 	tempArray = sumArrays(tempArray, x.item_change_in_budget);
	// 	return false;
	// });
	data.baseline_items.forEach((item) => {
		totalChange = sumArrays(totalChange, item.item_change_in_budget);
	});
	debugLog(4, "[scenarioUtils][PROCESS] getBudgetUpdate", { totalChange: totalChange });

	// data.baseline_change_in_budget = [
	// 	...tempArray.map((x) => precisionRound(x, data.baseline.currency_display_decimal)),
	// ];
	data.baseline_change_in_budget = [...totalChange];

	// Per-group budget breakdowns - Military Services, Domains and Categories
	groupWithBudget.forEach((g) => {
		data[`cfg_${g.code}`].forEach((cfgItem) => {
			let groupChange = new Array(data.baseline.number_of_period).fill(0);
			data.baseline_items.forEach((item) => {
				if (item[g.column] === cfgItem.id) {
					groupChange = sumArrays(groupChange, item.item_change_in_budget);
				}
			});
			cfgItem.change_in_budget = [...groupChange];
		});
	});

	return data;
};

/**
 * Determine a user's access level to a scenario.
 * Returns 2 for edit, 1 for read, 0 for none.
 *
 * @param {Object} item - The scenario object with scenario_accesses
 * @param {Array} groups - The user group list
 * @param {Object} user - The user object (should have .id)
 *
 * Special group IDs:
 *  -100: "Everyone"
 *  -99:  "All logged-in users"
 */
// Keep Scenario callers on their existing export while delegating the shared ACL logic.
export const scenarioAccess = (item, groups, user) =>
	evaluateAccessLevel(item, item?.scenario_accesses, groups, user, {
		publicLogMessage: "[scenarioAccess] PUBLIC \u00e2\u2020\u2019 READ",
		allLoginLogMessage: "[scenarioAccess] ALL_LOGIN",
		logIdKey: "baselineId",
	});
